import { useState, useEffect } from 'react';
import { getChapterCustomization, getUserByEmail, listActivitiesByChapter } from '@dataconnect/generated';
import { FaExclamationTriangle } from 'react-icons/fa';
import Constants from '../utilities/constants/Constants';
import activityImg from '../assets/images/activity.png';
import yellowImage from '../assets/images/yellow.png';

const PracticePage = ({ selectedStudent, onStartActivity, user, onSelectSyllabus, selectedConfig, loadingConfig }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchConfigAndActivities = async () => {
      if (!selectedStudent) {
        setError('No student selected');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // 1. Get internal user ID
        let internalUserId = null;
        if (user?.email) {
            const userResult = await getUserByEmail({ email: user.email });
            if (userResult.data?.users?.length) {
                internalUserId = userResult.data.users[0].id;
            }
        }

        // 2. Use the syllabus (selectedConfig) passed from props
        if (!selectedConfig) {
          // If not yet available from props, we might need to wait or show loading
          if (loadingConfig) {
            return; 
          }
          
          setLoading(false);
          return;
        }

        const config = selectedConfig;

        // 3. Parse syllabus to get chapter IDs
        const chapterConfigData = JSON.parse(config.chapterConfig || '{"chapters":[]}');
        const chapterIds = chapterConfigData.chapters?.map(ch => ch.chapterId) || [];

        if (chapterIds.length === 0) {
            setActivities([]);
            setLoading(false);
            return;
        }

        // 4. Fetch customizations for EACH chapter in the syllabus
        const allPracticeDataMap = {};
        
        await Promise.all(chapterIds.map(async (chapterId) => {
            try {
                // First check for customizations
                const customResult = await getChapterCustomization({
                    userId: internalUserId,
                    schoolId: selectedStudent?.school?.id || null,
                    chapterId: chapterId
                });

                let practiceDataMap = null;
                let usedCustomization = false;

                if (customResult.data) {
                    const userCustom = customResult.data.userCustomizations?.[0];
                    const schoolCustom = customResult.data.schoolCustomizations?.[0];
                    
                    const custom = (userCustom?.practiceData || userCustom?.activityData) ? userCustom : schoolCustom;

                    if (custom) {
                        if (custom.practiceData) {
                            try {
                                practiceDataMap = JSON.parse(custom.practiceData);
                                usedCustomization = true;
                            } catch (e) {
                                console.error(`Chapter ${chapterId}: ERROR parsing practiceData:`, e);
                            }
                        }
                        
                        // If practiceData failed or is missing, try to reconstruct from activityData
                        if (!practiceDataMap && custom.activityData) {
                            try {
                                const activityDataMap = JSON.parse(custom.activityData);
                                practiceDataMap = {}; 
                                usedCustomization = true;
                                
                                // Basic reconstruction
                                Object.values(activityDataMap).forEach(act => {
                                    if (act.activityType && act.content) {
                                        practiceDataMap[act.activityType] = {
                                            activityType: act.activityType,
                                            content: act.content
                                        };
                                    }
                                });
                            } catch (e) {
                                console.error(`Chapter ${chapterId}: ERROR parsing activityData:`, e);
                            }
                        }
                    }
                }

                // Fallback: If no customization exists, fetch base activities and merge them
                if (!usedCustomization) {
                    const activitiesResult = await listActivitiesByChapter({ chapterId });
                    if (activitiesResult.data?.activities) {
                        practiceDataMap = {};
                        activitiesResult.data.activities.forEach(act => {
                            const type = act.activityType;
                            try {
                                const content = JSON.parse(act.baseContent);
                                
                                if (!practiceDataMap[type]) {
                                    practiceDataMap[type] = {
                                        activityType: type,
                                        content: content
                                    };
                                } else {
                                    // Robust merge for fallback activities
                                    const existing = practiceDataMap[type].content;
                                    
                                    // Merge words
                                    if (Array.isArray(existing.words) && Array.isArray(content.words)) {
                                        content.words.forEach(w => {
                                            if (!existing.words.find(ew => (ew.languageWord || ew.word) === (w.languageWord || w.word))) {
                                                existing.words.push(w);
                                            }
                                        });
                                    } 
                                    
                                    // Merge questions
                                    if (Array.isArray(existing.questions) && Array.isArray(content.questions)) {
                                        content.questions.forEach(q => {
                                            if (!existing.questions.find(eq => (eq.question || eq.questionKannada) === (q.question || q.questionKannada))) {
                                                existing.questions.push(q);
                                            }
                                        });
                                    }

                                    // Merge pairs (Match/Opposites)
                                    if (Array.isArray(existing.pairs) && Array.isArray(content.pairs)) {
                                        content.pairs.forEach(p => {
                                            if (!existing.pairs.find(ep => ep.left === p.left && ep.right === p.right)) {
                                                existing.pairs.push(p);
                                            }
                                        });
                                    }

                                    // Merge sentences (Make Sentences)
                                    if (Array.isArray(existing.sentences) && Array.isArray(content.sentences)) {
                                        content.sentences.forEach(s => {
                                            if (!existing.sentences.find(es => es.englishText === s.englishText)) {
                                                existing.sentences.push(s);
                                            }
                                        });
                                    }

                                    // Merge items (generic)
                                    if (Array.isArray(existing.items) && Array.isArray(content.items)) {
                                        content.items.forEach(item => {
                                            const itemText = item.text || item.word || item.number || item.question;
                                            if (!existing.items.find(i => (i.text || i.word || i.number || i.question) === itemText)) {
                                                existing.items.push(item);
                                            }
                                        });
                                    }
                                }
                            } catch (parseErr) {
                                console.error(`Chapter ${chapterId}: ERROR parsing base activity content:`, parseErr);
                            }
                        });
                    }
                }

                // Merge into global map
                if (practiceDataMap) {
                    Object.entries(practiceDataMap).forEach(([type, data]) => {
                        if (!allPracticeDataMap[type]) {
                            allPracticeDataMap[type] = {
                                activityType: type,
                                content: JSON.parse(JSON.stringify(data.content))
                            };
                        } else {
                            // Robust merge logic
                            const existing = allPracticeDataMap[type].content;
                            const content = data.content;
                            
                            // Merge words
                            if (Array.isArray(existing.words) && Array.isArray(content.words)) {
                                content.words.forEach(w => {
                                    if (!existing.words.find(ew => (ew.languageWord || ew.word) === (w.languageWord || w.word))) {
                                        existing.words.push(w);
                                    }
                                });
                            } 
                            
                            // Merge questions
                            if (Array.isArray(existing.questions) && Array.isArray(content.questions)) {
                                content.questions.forEach(q => {
                                    if (!existing.questions.find(eq => (eq.question || eq.questionKannada) === (q.question || q.questionKannada))) {
                                        existing.questions.push(q);
                                    }
                                });
                            }

                            // Merge pairs (Match/Opposites/Who Said/Change Genders)
                            if (Array.isArray(existing.pairs) && Array.isArray(content.pairs)) {
                                content.pairs.forEach(p => {
                                    if (!existing.pairs.find(ep => 
                                        (ep.left === p.left && ep.right === p.right) || 
                                        (ep.word === p.word && ep.gender === p.gender)
                                    )) {
                                        existing.pairs.push(p);
                                    }
                                });
                            }

                            // Merge dialogues (Who Said To Whom)
                            if (Array.isArray(existing.dialogues) && Array.isArray(content.dialogues)) {
                                content.dialogues.forEach(d => {
                                    if (!existing.dialogues.find(ed => ed.text === d.text)) {
                                        existing.dialogues.push(d);
                                    }
                                });
                            }

                            // Merge groups (Tenses)
                            if (Array.isArray(existing.groups) && Array.isArray(content.groups)) {
                                content.groups.forEach(g => {
                                    const existingGroup = existing.groups.find(eg => eg.tense === g.tense);
                                    if (existingGroup) {
                                        if (Array.isArray(g.sentences)) {
                                            g.sentences.forEach(s => {
                                                if (!existingGroup.sentences.find(es => es.englishText === s.englishText)) {
                                                    existingGroup.sentences.push(s);
                                                }
                                            });
                                        }
                                    } else {
                                        existing.groups.push(JSON.parse(JSON.stringify(g)));
                                    }
                                });
                            }

                            // Merge sentences (Make Sentences)
                            if (Array.isArray(existing.sentences) && Array.isArray(content.sentences)) {
                                content.sentences.forEach(s => {
                                    if (!existing.sentences.find(es => es.englishText === s.englishText)) {
                                        existing.sentences.push(s);
                                    }
                                });
                            }

                            // Merge items (generic)
                            if (Array.isArray(existing.items) && Array.isArray(content.items)) {
                                content.items.forEach(item => {
                                    const itemText = item.text || item.word || item.number || item.question;
                                    if (!existing.items.find(i => (i.text || i.word || i.number || i.question) === itemText)) {
                                        existing.items.push(item);
                                    }
                                });
                            }
                        }
                    });
                }
            } catch (e) {
                console.error(`Failed to load activities for chapter ${chapterId}:`, e);
            }
        }));

        // 5. Transform merged practice data into activity objects
        const practiceActivities = Object.values(allPracticeDataMap).map(practiceActivity => {
          const typeConfig = Constants.ACTIVITY_TYPE_CONFIG[practiceActivity.activityType] || {
            title: practiceActivity.activityType,
            color: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            icon: "📝",
            hoverColor: "#6366f1"
          };

          return {
            ...typeConfig,
            id: `practice-${practiceActivity.activityType}`,
            activityType: practiceActivity.activityType,
            baseContent: JSON.stringify(practiceActivity.content),
            scopeLevel: 'CHAPTER',
            scopeId: null
          };
        });

        setActivities(practiceActivities);
      } catch (err) {
        console.error('Failed to load practice activities:', err);
        setError('Failed to load practice activities. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchConfigAndActivities();
  }, [selectedStudent?.selectedConfigId, user?.email, selectedConfig, loadingConfig]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Loading practice activities...</p>
        </div>
      </div>
    );
  }

  if (!selectedConfig && !loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex flex-col items-center justify-center p-6 text-center">
        <div className="bg-white/80 backdrop-blur-md rounded-3xl p-12 shadow-2xl border border-white/20 max-w-lg w-full transform hover:scale-[1.02] transition-transform duration-300">
          <div className="w-24 h-24 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-8 shadow-inner">
            <span className="text-5xl">📚</span>
          </div>
          <h2 className="text-3xl font-extrabold text-gray-900 mb-4 tracking-tight">No Syllabus Selected</h2>
          <p className="text-gray-600 text-lg mb-10 leading-relaxed">
            Please choose a syllabus to start your practice journey and access all your activities.
          </p>
          <button
            onClick={onSelectSyllabus}
            className="group relative w-full bg-gradient-to-r from-yellow-400 to-orange-500 text-white font-bold py-4 px-8 rounded-2xl shadow-xl hover:from-yellow-500 hover:to-orange-600 transition-all duration-300 transform active:scale-95"
          >
            <div className="flex items-center justify-center space-x-3">
              <span className="text-xl">✏️</span>
              <span>Select Your Syllabus</span>
              <span className="transform group-hover:translate-x-1 transition-transform">→</span>
            </div>
          </button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FaExclamationTriangle className="text-red-600 text-2xl" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">Oops! Something went wrong</h3>
          <p className="text-red-600 mb-6">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen"
      style={{
        backgroundImage: `url(${yellowImage})`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'top center',
        backgroundSize: 'cover'
      }}
    >
      {/* Main Content */}
      <div className="max-w-12xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        
        {/* Syllabus Selection Button - Moved from HomePage to here */}
        <div className="flex justify-end mb-8">
          {selectedStudent && (
              <button
                onClick={onSelectSyllabus}
                className="group relative bg-gradient-to-r from-yellow-400/90 to-orange-500/90 backdrop-blur-sm text-white py-2 px-6 rounded-xl shadow-lg shadow-yellow-400/20 border-2 border-yellow-300 hover:from-yellow-300 hover:to-orange-400 transition-all duration-300 transform hover:scale-105 h-[58px]"
              >
                <div className="flex items-center space-x-3">
                  <span className="text-xl">📚</span>
                  <div className="flex flex-col text-left">
                    <span className="text-[10px] text-yellow-100 font-medium uppercase tracking-wider">Current Syllabus</span>
                    <span className="text-sm font-bold">
                      {selectedStudent.selectedConfigId 
                        ? (loadingConfig 
                            ? 'Loading...' 
                            : (selectedConfig?.configName || (selectedConfig?.school 
                                ? `${selectedConfig.school.name} Syllabus` 
                                : 'Personal Syllabus')))
                        : 'Default Syllabus'
                      }
                    </span>
                  </div>
                  <span className="text-lg ml-2">✏️</span>
                </div>
                <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-yellow-300 to-orange-400 opacity-0 group-hover:opacity-20 blur-lg transition-opacity duration-300 pointer-events-none"></div>
              </button>
          )}
        </div>

        {/* Activities Grid */}
        {activities.length === 0 ? (
          <div className="bg-white/70 backdrop-blur-sm rounded-3xl shadow-xl border border-white/20 p-12 text-center max-w-2xl mx-auto">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <FaExclamationTriangle className="text-gray-400 text-3xl" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3">No activities available</h3>
            <p className="text-gray-600 text-lg">Please select a syllabus with activities to start practicing</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 py-8">
            {activities.map((activity, index) => (
              <button
                key={`${activity.id}-${index}`}
                type="button"
                onClick={() => onStartActivity(activity)}
                className="group relative bg-white/80 backdrop-blur-md rounded-3xl p-8 shadow-xl border border-white/20 transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:bg-white text-left overflow-hidden"
              >
                {/* Decorative background circle */}
                <div 
                  className="absolute -right-8 -top-8 w-32 h-32 rounded-full opacity-10 group-hover:opacity-20 transition-opacity duration-300"
                  style={{ background: activity.color || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
                />

                <div className="relative z-10 flex flex-col h-full">
                  {/* Activity Icon */}
                  <div className="w-16 h-16 rounded-2xl flex items-center justify-center text-4xl mb-6 shadow-lg transform group-hover:rotate-12 transition-transform duration-300"
                       style={{ background: activity.color || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                    {activity.icon}
                  </div>
                  
                  {/* Activity Title */}
                  <h3 className="text-2xl font-extrabold text-gray-900 mb-2 tracking-tight group-hover:text-indigo-600 transition-colors">
                    {activity.title}
                  </h3>
                  
                  {/* Activity Description (derived from type) */}
                  <p className="text-gray-600 text-sm leading-relaxed mb-6">
                    Practice your {activity.title.toLowerCase()} skills with interactive games and quizzes.
                  </p>

                  <div className="mt-auto flex items-center text-indigo-600 font-bold text-sm uppercase tracking-wider">
                    <span>Start Practice</span>
                    <span className="ml-2 transform group-hover:translate-x-2 transition-transform">→</span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PracticePage;
